import React, { useState, useEffect } from 'react';
import styles from './EnergiaYMineria.module.css';

const formatDate = (isoString) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `ÚLT. VALOR ${day}.${month}.${year} - ${hours}:${minutes}`;
};

const formatNumber = (val) => {
  if (val === undefined || val === null) return '-';
  return val.toLocaleString('es-AR');
};

const TickerSkeletonItem = () => (
  <div className={styles.item}>
    <div className={styles.itemHeader}>
      <div className={`${styles.skeleton} ${styles.skeletonTitle}`} />
      <div className={`${styles.skeleton} ${styles.skeletonBadge}`} />
    </div>
    <div className={styles.itemFooter}>
      <div className={`${styles.skeleton} ${styles.skeletonDate}`} />
      <div className={`${styles.skeleton} ${styles.skeletonValue}`} />
    </div>
    <div className={styles.verticalSeparator} />
  </div>
);

const TickerItem = ({ name, variation, value, tradeTime }) => {
  const isDown = variation < 0;
  const changeType = isDown ? 'down' : 'up';
  const arrow = isDown ? '▼' : '▲';
  const formattedVariation = `${Math.abs(variation).toFixed(2).replace('.', ',')}%`;

  return (
    <div className={styles.item}>
      <div className={styles.itemHeader}>
        <h3 className={styles.commodityName}>{name}</h3>
        <div className={`${styles.changeInfo} ${styles[changeType]}`}>
          <span className={styles.arrow}>{arrow}</span>
          <span className={styles.percentageChange}>{formattedVariation}</span>
        </div>
      </div>
      <div className={styles.itemFooter}>
        <p className={styles.timestamp}>{formatDate(tradeTime)}</p>
        <p className={styles.commodityValue}>{formatNumber(value)}</p>
      </div>
      <div className={styles.verticalSeparator} />
    </div>
  );
};

const TickerList = ({ items }) => (
  <div className={styles.tickerTrack}>
    {items?.map((item, index) => (
      <TickerItem
        key={`${item.id}-${index}`}
        name={item.name}
        variation={item.variation}
        value={item.value}
        tradeTime={item.trade_time}
      />
    ))}
  </div>
);

const itemsToExclude = [
  {
    "id": 629,
    "name": "Café"
  }
]

// Orden solicitado para los commodities: Brent; WTI; Gas Natural; Oro; Cobre
// (los ids corresponden a la API de mercados/asset-prices)
const commoditiesOrder = [628, 617, 618, 622, 624];

const sortCommodities = (list) => {
  return [...list].sort((a, b) => {
    const indexA = commoditiesOrder.indexOf(a.id);
    const indexB = commoditiesOrder.indexOf(b.id);
    if (indexA === -1 && indexB === -1) return 0;
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });
};

const EnergiaYMineria = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch('/api/mercados/asset-prices');
        if (!response.ok) throw new Error('Error al obtener los datos');

        const data = await response.json();
        const excludedIds = itemsToExclude.map((excluded) => excluded.id);
        const commoditiesList = sortCommodities(
          (data?.item?.commodities?.items || []).filter(
            (item) => !excludedIds.includes(item.id)
          )
        );
        const adrsList = data?.item?.adrs?.items || [];
        const list = [...commoditiesList, ...adrsList];
        setItems(list);
      } catch (error) {
        console.error('Error fetching mercados:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (!loading && !items.length) return null;

  return (
    <div className={styles.wrapper}>
      <div className={styles.mainContainer}>
        <div className={styles.tickerContainer}>
          {loading ? (
            <div className={styles.tickerSkeletonRow}>
              {Array.from({ length: 6 }).map((_, index) => (
                <TickerSkeletonItem key={`skeleton-${index}`} />
              ))}
            </div>
          ) : (
            <div className={styles.marqueeWrapper}>
              <TickerList items={items} /> {/* con dos TickerList, garantizando un loop continuo sin saltos visuales */}
              <TickerList items={items} />
            </div>
          )}
        </div>
      </div>
      <div className={styles.footerContainer}>
        <div className={styles.poweredByContainer}>
          <span className={styles.poweredByText}>INFORMACION DE</span>
          <a
            href="https://www.byma.com.ar/"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.iolLink}
          >
            <img
              src="/img/small_byma.png"
              alt="byma"
              className={styles.iolLogo}
            />
          </a>
        </div>
        <div className={styles.poweredByContainer}>
          <span className={styles.poweredByText}>PROVISTO POR</span>
          <a
            href="https://invertironline.com/"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.iolLink}
          >
            <img
              src="/img/Logo_IOL_InvertirOnline_Color.svg"
              alt="iol Inversiones"
              className={`${styles.iolLogo} ${styles.iolLogoColor}`}
            />
            <img
              src="/img/Logo_IOL_InvertirOnline_Blanco.svg"
              alt="iol Inversiones"
              className={`${styles.iolLogo} ${styles.iolLogoBlanco}`}
            />
          </a>
          <span className={styles.delayText}>con 20 minutos de diferido*</span>
        </div>
      </div>
    </div>
  );
};

export default EnergiaYMineria;
